<script setup lang="ts">
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useSettingStore from '../../stores/setting';
import useMediaStore from '../../stores/media';
import useUiStore from '../../stores/ui';
import useAudioAnnotations from '../../composables/use-audio-annotations';
import useLocale from '../../composables/useLocale';
import { useDisplay, useTheme } from 'vuetify';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPalette } from '@fortawesome/pro-solid-svg-icons';
library.add(faPalette);

type Props = {
  disabled?: boolean;
};
withDefaults(defineProps<Props>(), {
  disabled: false,
});

const { t } = useLocale();
const { smAndDown } = useDisplay();
const theme = useTheme();
const settingStore = useSettingStore();
const mediaStore = useMediaStore();
const { audioHighlightColor, audioHighlightBlendMode, hasAudioTracking } = storeToRefs(settingStore);
const { currentTrack, isPlaying } = storeToRefs(mediaStore);
const { isReaderVisible } = storeToRefs(useUiStore());
const { highlightPlayingTrack, clearPlayingHighlight } = useAudioAnnotations();

// Computed property to check if current theme is dark
const isDarkTheme = computed(() => theme.global.current.value.dark);

// Color mapping between light and dark modes (WCAG 2.2 AA, all lowercase for case-insensitive lookup)
const colorMapping = {
  // Light → Dark
  '#fde68a': '#b45309', // Yellow → Amber
  '#fbcfe8': '#be185d', // Rose → Berry
  '#bfdbfe': '#1d4ed8', // Cerulean → Cobalt
  '#99f6e4': '#0f766e', // Aqua → Deep Teal
  // Dark → Light
  '#b45309': '#fde68a',
  '#be185d': '#fbcfe8',
  '#1d4ed8': '#bfdbfe',
  '#0f766e': '#99f6e4',
};

// Light mode: soft tones, multiply blend mode safe
const lightModeColors = [
  { value: '#fde68a', translationKey: 'book_player.label_audio_highlight_color_yellow' },
  { value: '#fbcfe8', translationKey: 'book_player.label_audio_highlight_color_red' },
  { value: '#bfdbfe', translationKey: 'book_player.label_audio_highlight_color_blue' },
  { value: '#99f6e4', translationKey: 'book_player.label_audio_highlight_color_green' },
];

// Dark mode: saturated tones, screen blend mode safe
const darkModeColors = [
  { value: '#b45309', translationKey: 'book_player.label_audio_highlight_color_dark_yellow' },
  { value: '#be185d', translationKey: 'book_player.label_audio_highlight_color_dark_red' },
  { value: '#1d4ed8', translationKey: 'book_player.label_audio_highlight_color_dark_blue' },
  { value: '#0f766e', translationKey: 'book_player.label_audio_highlight_color_dark_green' },
];

// Use the appropriate palette based on theme
const allColors = computed(() => (isDarkTheme.value ? darkModeColors : lightModeColors));

const selectColor = (color: string) => {
  audioHighlightColor.value = color;
};

const isColorActive = (color: string) => {
  return audioHighlightColor.value.toLowerCase() === color.toLowerCase();
};

// Update blend mode based on theme
watch(
  isDarkTheme,
  (isDark) => {
    audioHighlightBlendMode.value = isDark ? 'screen' : 'multiply';
  },
  { immediate: true },
);

// Watch for color changes and refresh the highlight
watch(audioHighlightColor, (newColor, oldColor) => {
  // Only refresh if color actually changed and audio is playing with tracking enabled
  if (
    oldColor !== undefined &&
    newColor !== oldColor &&
    currentTrack.value &&
    hasAudioTracking.value &&
    isReaderVisible.value &&
    isPlaying.value
  ) {
    console.debug('Audio highlight color changed, refreshing highlight');
    clearPlayingHighlight();
    setTimeout(() => {
      if (currentTrack.value) {
        highlightPlayingTrack(currentTrack.value);
      }
    }, 50);
  }
});

// Watch for theme changes - auto-switch color and refresh the highlight
watch(isDarkTheme, (newTheme, oldTheme) => {
  if (oldTheme === undefined || newTheme === oldTheme) return;

  // Auto-switch to the equivalent color for the new theme
  const currentColor = audioHighlightColor.value.toLowerCase();
  const mappedColor = colorMapping[currentColor as keyof typeof colorMapping];

  if (mappedColor) {
    console.debug(`Theme changed, switching color from ${currentColor} to ${mappedColor}`);
    audioHighlightColor.value = mappedColor;
  }

  // Refresh the highlight if audio is playing with tracking enabled
  if (currentTrack.value && hasAudioTracking.value && isReaderVisible.value && isPlaying.value) {
    console.debug('Refreshing audio highlight with new theme color');
    clearPlayingHighlight();
    setTimeout(() => {
      if (currentTrack.value) {
        highlightPlayingTrack(currentTrack.value);
      }
    }, 50);
  }
});
</script>

<template>
  <v-list-item
    density="compact"
    data-testid="bp-setting-audio-highlight-color"
    :title="t('book_player.label_audio_highlight_color')"
    :disabled="disabled"
  >
    <template #prepend>
      <FontAwesomeIcon :size="!smAndDown ? 'xl' : 'lg'" icon="fa-solid fa-palette" class="pr-3" />
    </template>
    <template #append>
      <div class="color-picker-container my-1 ml-4">
        <v-btn
          v-for="color in allColors"
          :key="color.value"
          variant="text"
          :ripple="false"
          icon
          size="small"
          :title="t(color.translationKey)"
          :disabled="disabled"
          @click="selectColor(color.value)"
        >
          <div
            class="color-ball"
            :class="{ active: isColorActive(color.value) }"
            :style="{ backgroundColor: color.value }"
          />
          <v-tooltip activator="parent" location="bottom" :text="t(color.translationKey)" />
        </v-btn>
      </div>
    </template>
  </v-list-item>
</template>

<style scoped lang="scss">
.color-picker-container {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-width: 180px;
}

.color-ball {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid white;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover:not(.active) {
    transform: scale(1.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &.active {
    width: 32px;
    height: 32px;
    border: 3px solid rgb(var(--v-theme-primary));
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
}

:deep(.v-icon) {
  opacity: 1 !important;
}
</style>
